/**
 * Map Store - Pinia
 *
 * Manages map control signals for coordination between components.
 * Migrated from VueX module src/store/modules/map/
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';

export const useMapStore = defineStore('map', {
  state: () => ({
    // OpenLayers map instance (shared for cross-component access)
    map: null,

    // Map control signals - increment to trigger actions in watchers
    centerMapSignal: 0,
    deleteTrackLayerSignal: 0,
    lockViewToCenterSignal: null,
    removeRecordedTrackSignal: 0,
    switchTrackSignal: null,
    updateTrackGeometrySignal: null,
    toggleRecordedTrackSignal: null,
    zoomIntoCenterSignal: false,
    displayModeTransition: null,

    // Direction indicator state (for track display)
    // Default to visible on page load
    showDirectionArrows: true,
    showStartEndMarkers: true,
    isTrackReversed: false,
    directionIndicatorsSignal: 0,
    animationSignal: 0,

    // Track trimming mode state (indices only - no coordinates stored)
    trackTrimmingMode: false,
    trimStartIndex: 0,
    trimEndIndex: 0,
    trimCoordinateCount: 0, // Total coordinate count for validation
    trimPreviewSignal: 0,

    // NOTE: Trim mode composable is now shared via provide/inject (MapContainerDisplay -> TrackedRoute)

    // Shadow track overlay state (for showing original track behind trimmed)
    showOriginalShadow: false,
    originalShadowCoordinates: [], // EPSG:4326 coordinates for shadow display
    shadowTrackSignal: 0,

    // Connector lines state (for collected/merged tracks)
    showConnectorLines: true,
    hasConnectorLines: false,
    connectorLinesSignal: 0,

    // Multi-track display signal (for merge preview)
    displayMultipleTracksSignal: null,
    clearMultiTrackSignal: 0,

    // Multi-track display state (for cross-component access, e.g., MultiTrackDetails)
    multiTrackDisplayTracks: [],
    multiTrackActiveIndex: 0,
    multiTrackHighlightSignal: null,

    // Compare-specific: active track ID for opacity control
    compareActiveTrackId: null
  }),

  getters: {
    getCenterMapSignal: (state) => state.centerMapSignal,
    getDeleteTrackLayerSignal: (state) => state.deleteTrackLayerSignal,
    getLockViewToCenterSignal: (state) => state.lockViewToCenterSignal,
    getRemoveRecordedTrackSignal: (state) => state.removeRecordedTrackSignal,
    getSwitchTrackSignal: (state) => state.switchTrackSignal,
    getUpdateTrackGeometrySignal: (state) => state.updateTrackGeometrySignal,
    getToggleRecordedTrackSignal: (state) => state.toggleRecordedTrackSignal,
    getZoomIntoCenterSignal: (state) => state.zoomIntoCenterSignal,
    getDisplayModeTransition: (state) => state.displayModeTransition,

    // Multi-track display getters
    multiTrackCount: (state) => state.multiTrackDisplayTracks.length,
    multiTrackActiveTrack: (state) => {
      if (state.multiTrackDisplayTracks.length === 0) return null;
      return state.multiTrackDisplayTracks[state.multiTrackActiveIndex] || state.multiTrackDisplayTracks[0];
    }
  },

  actions: {
    /**
     * Center the map view
     */
    centerMap () {
      this.centerMapSignal++;
    },

    /**
     * Delete the track layer from the map
     */
    deleteTrackLayer () {
      this.deleteTrackLayerSignal++;
    },

    /**
     * Lock map view to center when recording
     */
    lockViewToCenter (geojson) {
      this.lockViewToCenterSignal = { data: geojson, timestamp: Date.now() };
    },

    /**
     * Remove the recorded track from the map
     */
    removeRecordedTrack () {
      this.removeRecordedTrackSignal++;
    },

    /**
     * Switch to a new track (removes old, shows new with zoom)
     * @param {Object} geojson - GeoJSON feature data
     * @param {string} [typeTitle] - Optional activity type title for color
     */
    switchTrack (geojson, typeTitle = null) {
      this.switchTrackSignal = { data: geojson, typeTitle, timestamp: Date.now() };
    },

    /**
     * Update current track geometry on map without fitting/zooming the view
     * @param {Object} geojson - GeoJSON feature data
     * @param {string} [typeTitle] - Optional activity type title for color
     */
    updateTrackGeometry (geojson, typeTitle = null) {
      this.updateTrackGeometrySignal = { data: geojson, typeTitle, timestamp: Date.now() };
    },

    /**
     * Toggle the recorded track visibility
     * @param {Object} geojson - GeoJSON feature data
     * @param {string} [typeTitle] - Optional activity type title for color
     */
    toggleRecordedTrack (geojson, typeTitle = null) {
      this.toggleRecordedTrackSignal = { data: geojson, typeTitle, timestamp: Date.now() };
    },

    /**
     * Clear the switch track signal (used when navigating away from track views)
     */
    clearSwitchTrackSignal () {
      this.switchTrackSignal = null;
    },

    /**
     * Display multiple tracks on the map (for merge preview)
     * @param {Array} tracks - Array of track objects with geojson data
     */
    setCompareActiveTrackId (id) {
      this.compareActiveTrackId = id;
    },

    displayMultipleTracks (tracks, options = {}) {
      appDebugLog.log('[MapStore] displayMultipleTracks called with', tracks?.length || 0, 'tracks');
      this.displayMultipleTracksSignal = { data: tracks, colorMap: options.colorMap || null, timestamp: Date.now() };
    },

    /**
     * Update multi-track display state (called by useMultiTrackDisplay composable)
     * @param {Array} tracks - Array of track objects currently displayed
     * @param {number} activeIndex - Index of the active/highlighted track
     */
    setMultiTrackDisplayState (tracks, activeIndex = 0) {
      this.multiTrackDisplayTracks = tracks || [];
      this.multiTrackActiveIndex = activeIndex;
    },

    /**
     * Set the active track index in multi-track display
     * @param {number} index - New active index
     */
    setMultiTrackActiveIndex (index) {
      if (index >= 0 && index < this.multiTrackDisplayTracks.length) {
        this.multiTrackActiveIndex = index;
      }
    },

    /**
     * Trigger highlighting of a track on the map
     * @param {number} index - Track index to highlight
     */
    highlightMultiTrack (index) {
      this.multiTrackHighlightSignal = { index, timestamp: Date.now() };
    },

    /**
     * Clear multi-track display (removes all tracks from merge preview)
     * Directly removes layers from map and clears state
     */
    clearMultiTrackDisplay () {
      appDebugLog.log('[MapStore] clearMultiTrackDisplay called');

      // Directly remove multi-track layers from the map
      if (this.map) {
        const layersToRemove = [];
        this.map.getLayers().forEach((layer) => {
          if (layer.get && layer.get('isMultiTrackLayer')) {
            layersToRemove.push(layer);
          }
        });

        layersToRemove.forEach((layer) => {
          this.map.removeLayer(layer);
          appDebugLog.log('[MapStore] Removed multi-track layer from map');
        });

        appDebugLog.log('[MapStore] Removed', layersToRemove.length, 'multi-track layers');
      }

      // Clear state
      this.multiTrackDisplayTracks = [];
      this.multiTrackActiveIndex = 0;
      this.clearMultiTrackSignal++;
    },

    /**
     * Set zoom into center flag
     */
    setZoomIntoCenter (bool) {
      this.zoomIntoCenterSignal = bool;
    },

    /**
     * Coordinate animated transitions between 2D OpenLayers and 3D Cesium views.
     * @param {Object|null} payload
     */
    setDisplayModeTransition (payload) {
      this.displayModeTransition = payload
        ? { ...payload, timestamp: Date.now() }
        : null;
    },

    /**
     * Update only the current transition phase while keeping the rest of the payload.
     * @param {string} phase
     */
    setDisplayModeTransitionPhase (phase) {
      if (!this.displayModeTransition) return;
      this.displayModeTransition = {
        ...this.displayModeTransition,
        phase,
        timestamp: Date.now()
      };
    },

    clearDisplayModeTransition () {
      this.displayModeTransition = null;
    },

    /**
     * Set the OpenLayers map instance (called by MapContainer on mount)
     * @param {Object} map - OpenLayers Map instance
     */
    setMap (map) {
      this.map = map;
    },

    // ============================================================================
    // Direction Indicator Actions
    // ============================================================================

    /**
     * Toggle direction arrows visibility
     */
    toggleDirectionArrows () {
      this.showDirectionArrows = !this.showDirectionArrows;
      this.directionIndicatorsSignal++;
    },

    /**
     * Toggle start/end markers visibility
     */
    toggleStartEndMarkers () {
      this.showStartEndMarkers = !this.showStartEndMarkers;
      this.directionIndicatorsSignal++;
    },

    /**
     * Reverse track direction for display
     */
    reverseTrackDirection () {
      this.isTrackReversed = !this.isTrackReversed;
      this.directionIndicatorsSignal++;
    },

    /**
     * Reset all direction indicators to default state
     * Defaults to visible (matching initial state)
     */
    resetDirectionIndicators () {
      // Reset to default state (direction indicators visible by default)
      this.showDirectionArrows = true;
      this.showStartEndMarkers = true;
      this.isTrackReversed = false;
    },

    /**
     * Toggle animation for direction dots
     */
    toggleAnimatedDots () {
      this.animationSignal++;
    },

    // ============================================================================
    // Track Trimming Actions
    // ============================================================================

    /**
     * Enter track trimming mode with indices only (no coordinates stored)
     * @param {number} coordinateCount - Total number of coordinates in the track
     */
    enterTrackTrimmingMode (coordinateCount) {
      this.trackTrimmingMode = true;
      this.trimStartIndex = 0;
      this.trimEndIndex = coordinateCount - 1;
      this.trimCoordinateCount = coordinateCount;
      // Ensure direction indicators are visible
      this.showDirectionArrows = true;
      this.showStartEndMarkers = true;
      this.directionIndicatorsSignal++;
    },

    /**
     * Exit track trimming mode without saving
     */
    cancelTrackTrimmingMode () {
      this.trackTrimmingMode = false;
      this.trimStartIndex = 0;
      this.trimEndIndex = 0;
      this.trimCoordinateCount = 0;
      this.trimPreviewSignal++;
    },

    /**
     * Update trim indices (called when markers are dragged)
     * @param {number} startIndex - New start index
     * @param {number} endIndex - New end index
     */
    updateTrimIndices (startIndex, endIndex) {
      this.trimStartIndex = startIndex;
      this.trimEndIndex = endIndex;
      this.trimPreviewSignal++;
    },

    /**
     * Toggle showing original track as shadow overlay
     * @param {Array} coordinates - EPSG:4326 coordinates to show as shadow
     * @param {boolean} show - Whether to show or hide the shadow
     */
    toggleOriginalShadow (coordinates, show) {
      this.showOriginalShadow = show;
      this.originalShadowCoordinates = coordinates;
      this.shadowTrackSignal++;
    },

    // ============================================================================
    // Connector Lines Actions (for collected/merged tracks)
    // ============================================================================

    /**
     * Set whether the current track has connector lines
     * @param {boolean} hasConnectors - Whether connectors exist
     */
    setHasConnectorLines (hasConnectors) {
      this.hasConnectorLines = hasConnectors;
    },

    /**
     * Toggle connector lines visibility
     */
    toggleConnectorLines () {
      if (!this.hasConnectorLines) return;
      this.showConnectorLines = !this.showConnectorLines;
      this.connectorLinesSignal++;
    },

    /**
     * Reset connector lines state (when switching tracks)
     */
    resetConnectorLines () {
      this.hasConnectorLines = false;
      this.showConnectorLines = true; // Default to visible
    }
  }
});
